/**
 * Live-connection retry policy for the graphql-ws client (wired in
 * `~/graphql/index.tsx`). Pure and dependency-free so it is unit-testable
 * without a browser or a socket — the same split as `auth-recovery.ts`.
 *
 * Why this exists (Sentry WEB-SPACE-1WH, 1,422 people in 30 days): graphql-ws
 * only retries what its `shouldRetry` option accepts, and the library default
 * accepts close events only. A browser WebSocket that fails "the WebSocket
 * connection" (network drop, corporate proxy reset, sleep/wake) fires an
 * `error` event FIRST, which is not close-shaped — so every live subscription
 * on the tab was errored with `Socket closed` and never retried. Apollo never
 * restarts an errored subscription on its own, so notifications, points,
 * highlight sync and AI streams stayed dead for the rest of the session.
 */

import { CloseCode } from "graphql-ws";

/** graphql-ws `CloseCode.Forbidden`: the server asks the client to reconnect with fresh credentials. */
export const WS_REAUTH_CLOSE_CODE: number = CloseCode.Forbidden;

/**
 * Close codes graphql-ws itself treats as fatal (it never consults
 * `shouldRetry` for them) — its explicit list, derived from the enum so a
 * library bump cannot desynchronise us, plus the standard 1000–1999 range
 * minus the codes it deliberately retries (see `isFatalInternalCloseCode` in
 * graphql-ws/client).
 */
const FATAL_PROTOCOL_CLOSE_CODES = new Set<number>([
  CloseCode.InternalServerError,
  CloseCode.InternalClientError,
  CloseCode.BadRequest,
  CloseCode.BadResponse,
  CloseCode.Unauthorized,
  CloseCode.SubprotocolNotAcceptable,
  CloseCode.SubscriberAlreadyExists,
  CloseCode.TooManyInitialisationRequests,
]);
const RETRIED_STANDARD_CLOSE_CODES = new Set([1000, 1001, 1005, 1006, 1012, 1013, 1014]);

function isCloseLike(value: unknown): value is { code: number; reason: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    "reason" in value
  );
}

function isWebSocketErrorEvent(value: unknown): boolean {
  // The WebSocket `error` DOM event: `type === "error"` with a socket-shaped
  // target (numeric readyState). Kept structural so node tests can pass a
  // plain object; anything else that merely has a `type` is not retried.
  if (typeof value !== "object" || value === null) return false;
  const event = value as { type?: unknown; target?: unknown };
  if (event.type !== "error") return false;
  const target = event.target as { readyState?: unknown } | null | undefined;
  return typeof target?.readyState === "number";
}

/**
 * Retry a transport failure, never a programming error.
 *
 *  - close events: retry (graphql-ws has already filtered its fatal codes
 *    before asking us, so 4500/4401/… stay terminal).
 *  - the browser's `error` event: retry — this is the incident.
 *  - anything else (an `Error` thrown by `connectionParams`, the URL
 *    resolver or the message parser): terminal, so a genuine client bug is
 *    surfaced instead of being retried forever.
 */
export function shouldRetryWsFailure(errOrCloseEvent: unknown): boolean {
  if (isCloseLike(errOrCloseEvent)) return true;
  if (errOrCloseEvent instanceof Error) return false;
  return isWebSocketErrorEvent(errOrCloseEvent);
}

export const WS_RETRY_BASE_MS = 1_000;
export const WS_RETRY_CAP_MS = 10_000;
export const WS_RETRY_JITTER_MS = 300;

/**
 * Capped, jittered exponential backoff: 1 s, 2 s, 4 s, 8 s, then 10 s, each
 * plus up to 300 ms of jitter. graphql-ws resets `retries` to 0 on every
 * successful `connection_ack`, so a healthy session that drops once (or is
 * `terminate()`d by the auth recovery) reconnects in about a second; only a
 * sustained failure backs off, which caps a dead endpoint at ~6 attempts a
 * minute per tab instead of the previous fixed 60.
 */
export function wsRetryWait(retries: number, rand: () => number = Math.random): number {
  const step = Math.max(0, Math.floor(retries));
  const base = Math.min(WS_RETRY_BASE_MS * 2 ** step, WS_RETRY_CAP_MS);
  return base + Math.floor(rand() * WS_RETRY_JITTER_MS);
}

export function isReauthClose(code: number): boolean {
  return code === WS_REAUTH_CLOSE_CODE;
}

export function isFatalWsCloseCode(code: number): boolean {
  if (RETRIED_STANDARD_CLOSE_CODES.has(code)) return false;
  if (code >= 1000 && code <= 1999) return true;
  return FATAL_PROTOCOL_CLOSE_CODES.has(code);
}

/**
 * How long after a re-auth close a second one still counts as "in a row".
 * graphql-ws resets its own retry counter on every connection_ack, and the
 * server's 4403 always follows an ack (it is sent per Subscribe), so neither
 * the library's counter nor a "reset on connected" counter can see a loop.
 * Time between 4403s can: an expired token gives one 4403 and then a good
 * session (the next one is an hour away); a revoked token gives one every
 * reconnect.
 */
export const WS_REAUTH_STREAK_WINDOW_MS = 60_000;

/**
 * Tracks consecutive re-auth closes and turns them into a backoff level, so a
 * credential the server keeps rejecting backs off like a dead endpoint would
 * (and hands over to the auth recovery — see `~/graphql/index.tsx`).
 */
export function createReauthStreak(now: () => number = Date.now) {
  let count = 0;
  let lastAt = 0;
  return {
    /** Record a re-auth close; returns the streak length including this one. */
    record(): number {
      const t = now();
      count = t - lastAt > WS_REAUTH_STREAK_WINDOW_MS ? 1 : count + 1;
      lastAt = t;
      return count;
    },
    /** Backoff level to feed `wsRetryWait` (0 = no escalation). */
    backoffLevel(): number {
      if (count === 0 || now() - lastAt > WS_REAUTH_STREAK_WINDOW_MS) return 0;
      return Math.max(0, count - 1);
    },
    count(): number {
      return count;
    },
  };
}
